import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Group,
  HStack,
  Portal,
  RadioCard,
  Select,
  Separator,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiCalendar } from 'react-icons/ci';
import { FaPlus } from 'react-icons/fa';
import { GoPlus } from 'react-icons/go';
import { IoCopyOutline } from 'react-icons/io5';

import { IndicatorsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/index.tsx';

function getIndicatorsProgressionQueryOptions({ slug }: { slug: string }) {
  return {
    queryKey: ['indicators_progression'],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorsProgression({
        companySlug: slug,
      });
    },
  };
}

export const CollecteCreateDialog = () => {
  const navigate = useNavigate({ from: Route.fullPath });
  const [open, setOpen] = useState(false);
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState('create');
  const [selectedSubMethod, setSelectedSubMethod] = useState<string | null>(null);
  const [step, setStep] = useState<'method' | 'create-options'>('method');
  const showToast = useCustomToast();

  const { data: indicatorsProgression } = useQuery({
    ...getIndicatorsProgressionQueryOptions({ slug }),
  });

  const [instantiatedYear, setInstantiatedYear] = useState<string[]>([]);
  const [notInstantiatedYear, setNotInstantiatedYear] = useState<string[]>([]);

  const uniqueYearsInstantiated = [...new Set(indicatorsProgression?.data.map((el) => el.year) ?? [])];

  const generateYearRange = (year: number) => {
    const startYear = year - 15;
    const endYear = year + 15;
    const range = [];

    // Add years from (year - 15) to (year + 15)
    for (let i = startYear; i <= endYear; i++) {
      range.push(i);
    }

    return range;
  };

  const instantiatedYearCollection = createListCollection({
    items: [
      ...uniqueYearsInstantiated.map((year) => ({
        label: year.toString(),
        value: year,
      })),
    ],
  });

  const notInstantiatedYearCollection = createListCollection({
    items: [
      ...generateYearRange(new Date().getFullYear())
        .map((year) => ({
          label: year.toString(),
          value: year,
        }))
        .filter((item) => !uniqueYearsInstantiated.includes(item.value)),
    ],
  });

  const duplicateYearMutation = useMutation({
    mutationFn: ({ sourceYear, targetYear }: { sourceYear: number; targetYear: number }) =>
      IndicatorsService.duplicateYear({
        companySlug: slug,
        sourceYear,
        targetYear,
      }),
    onSuccess: () => {
      setOpen(false);
      showToast('Success!', 'Year duplicated successfully.', 'success');
      setNotInstantiatedYear([]);
      setInstantiatedYear([]);
    },
    onError: () => {
      showToast("Something's wrong!", 'Cannot duplicate this year.', 'error');
    },
  });

  const handleCreateYearCollecte = async () => {
    if (notInstantiatedYear.length > 0 && instantiatedYear.length === 0) {
      navigate({
        search: () => ({
          year: notInstantiatedYear[0].toString(),
          period: 'year1',
          entity_id: '',
        }),
      });
      setOpen(false);
      setNotInstantiatedYear([]);
    }
    if (instantiatedYear.length > 0) {
      duplicateYearMutation.mutate({
        sourceYear: parseInt(instantiatedYear[0]),
        targetYear: parseInt(notInstantiatedYear[0]),
      });
    }
  };

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>
        <Button bg={'primary'} size="sm">
          <FaPlus /> {t('collection.manage.headers.create.year.collect')}
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>
                <Flex alignItems={'center'} gap={2}>
                  <CiCalendar />
                  {t('collection.manage.headers.create.year.collect')}
                </Flex>
              </Dialog.Title>
            </Dialog.Header>
            {step === 'method' && (
              <Dialog.Body zIndex={1}>
                <Box>
                  <Select.Root
                    collection={notInstantiatedYearCollection}
                    size="sm"
                    value={notInstantiatedYear}
                    onValueChange={(e) => {
                      setNotInstantiatedYear(e.value);
                    }}
                  >
                    <Select.HiddenSelect />
                    <Select.Label>Année a creer</Select.Label>
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={t('collection.manage.headers.year.select.placeholder')} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {notInstantiatedYearCollection.items.map((year) => (
                          <Select.Item item={year} key={year.value}>
                            {year.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Box>
                <HStack my={4}>
                  <Separator flex="1" />
                </HStack>
                <Box>
                  <RadioCard.Root
                    value={selectedMethod}
                    onValueChange={(e) => {
                      setSelectedMethod(e.value!.toString());
                    }}
                    gap="4"
                    colorPalette="blue"
                  >
                    <RadioCard.Label>Méthode de création</RadioCard.Label>
                    <Group orientation="vertical">
                      {items.map((item) => (
                        <RadioCard.Item key={item.value} value={item.value} width="full" borderRadius="8px">
                          <RadioCard.ItemHiddenInput />
                          <RadioCard.ItemControl>
                            <RadioCard.ItemIndicator />
                            <RadioCard.ItemContent>
                              <RadioCard.ItemText>
                                <Flex alignItems="center" gap={1}>
                                  {item.icon}
                                  {item.title}
                                </Flex>
                              </RadioCard.ItemText>
                              <RadioCard.ItemDescription>{item.description}</RadioCard.ItemDescription>
                              {selectedMethod === 'duplicate' && item.value === 'duplicate' && (
                                <Select.Root
                                  collection={instantiatedYearCollection}
                                  size="sm"
                                  value={instantiatedYear}
                                  onValueChange={(e) => {
                                    setInstantiatedYear(e.value);
                                  }}
                                >
                                  <Select.HiddenSelect />
                                  <Select.Label>Année source a dupliquer</Select.Label>
                                  <Select.Control>
                                    <Select.Trigger>
                                      <Select.ValueText
                                        placeholder={t('collection.manage.headers.duplicateYear.select.placeholder')}
                                      />
                                    </Select.Trigger>
                                    <Select.IndicatorGroup>
                                      <Select.ClearTrigger />
                                      <Select.Indicator />
                                    </Select.IndicatorGroup>
                                  </Select.Control>
                                  <Select.Positioner>
                                    <Select.Content>
                                      {instantiatedYearCollection.items.map((year) => (
                                        <Select.Item item={year} key={year.value}>
                                          {year.label}
                                          <Select.ItemIndicator />
                                        </Select.Item>
                                      ))}
                                    </Select.Content>
                                  </Select.Positioner>
                                </Select.Root>
                              )}
                            </RadioCard.ItemContent>
                          </RadioCard.ItemControl>
                        </RadioCard.Item>
                      ))}
                    </Group>
                  </RadioCard.Root>
                </Box>
              </Dialog.Body>
            )}
            {step === 'create-options' && (
              <Dialog.Body>
                <Box mt={6}>
                  <Text fontWeight="bold" mb={2}>
                    Choisissez un mode de création
                  </Text>
                  <RadioCard.Root
                    value={selectedSubMethod || ''}
                    onValueChange={(e) => setSelectedSubMethod(e.value!.toString())}
                    gap="4"
                    colorPalette={'primary'}
                  >
                    <Group orientation="vertical">
                      {createFromScratchOptions.map((opt) => (
                        <RadioCard.Item
                          key={opt.value}
                          value={opt.value}
                          width="full"
                          borderRadius="8px"
                          disabled={opt.disabled}
                        >
                          <RadioCard.ItemHiddenInput />
                          <RadioCard.ItemControl>
                            <RadioCard.ItemIndicator />
                            <RadioCard.ItemContent>
                              <RadioCard.ItemText>{opt.title}</RadioCard.ItemText>
                              <RadioCard.ItemDescription>{opt.description}</RadioCard.ItemDescription>
                            </RadioCard.ItemContent>
                          </RadioCard.ItemControl>
                        </RadioCard.Item>
                      ))}
                    </Group>
                  </RadioCard.Root>
                </Box>
              </Dialog.Body>
            )}
            <Dialog.Footer>
              {step === 'create-options' ? (
                <Button onClick={() => setStep('method')} variant="outline">
                  Retour
                </Button>
              ) : (
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">{t('ui.cancel')}</Button>
                </Dialog.ActionTrigger>
              )}
              {step === 'method' && (
                <Button
                  onClick={() => {
                    if (selectedMethod === 'create') {
                      if (notInstantiatedYear[0] === undefined) {
                        showToast("Pas d'année selectionnée", 'Vous devez séléctionner une année a creer', 'error');
                      } else {
                        setStep('create-options');
                      }
                    } else {
                      handleCreateYearCollecte();
                    }
                  }}
                >
                  Suivant
                </Button>
              )}
              {step === 'create-options' && (
                <Button
                  onClick={() => {
                    handleCreateYearCollecte();
                    if (selectedSubMethod === 'add-indicator') {
                      navigate({
                        to: '/$slug/indicators/collecte/manage',
                        params: { slug },
                        search: {
                          year: String(notInstantiatedYear),
                          period: `year1`,
                          entity_id: '',
                          page: 1,
                          pageSize: 25,
                          filter: '',
                          sort: '',
                          group_by: '',
                          search: '',
                          mode: 'select',
                        },
                      });
                    }
                  }}
                >
                  {t('ui.save')}
                </Button>
              )}
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const items = [
  {
    value: 'create',
    icon: <GoPlus />,
    title: 'Créer a partir de zero',
    description: 'Créer une nouvelle année de collecte vide, sans aucune configuration préexistante',
  },
  {
    value: 'duplicate',
    icon: <IoCopyOutline />,
    title: 'Dupliquer depuis une année existante',
    description: "Copier toutes les configurations d'indicateurs d'une année existante",
  },
];

export default CollecteCreateDialog;

const createFromScratchOptions = [
  {
    value: 'add-indicator',
    title: 'Ajouter des indicateurs dès maintenant',
    description: 'Commencer avec une collecte complètement vide, sans indicateurs.',
  },
  {
    value: 'empty',
    disabled: true,
    title: "Créer l'année sans indicateurs",
    description: 'Votre année de collecte est créée sans indicateurs, il sera possible de les ajouter ultérieurement',
  },
];

export const CreateOptionsSlide = ({ selectedMethod }: { selectedMethod: string }) => {
  const [selectedSubMethod, setSelectedSubMethod] = useState<string | null>(null);

  return (
    <AnimatePresence>
      {selectedMethod === 'create' && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <Box mt={6} p={4} borderWidth="1px" borderRadius="12px" shadow="md" bg="gray.50">
            <Text fontWeight="bold" mb={2}>
              Choisissez un mode de création
            </Text>
            <RadioCard.Root
              value={selectedSubMethod || ''}
              onValueChange={(e) => setSelectedSubMethod(e.value!.toString())}
              gap="4"
              colorPalette="purple"
            >
              <Group orientation="vertical">
                {createFromScratchOptions.map((opt) => (
                  <RadioCard.Item key={opt.value} value={opt.value} width="full" borderRadius="8px">
                    <RadioCard.ItemHiddenInput />
                    <RadioCard.ItemControl>
                      <RadioCard.ItemIndicator />
                      <RadioCard.ItemContent>
                        <RadioCard.ItemText>{opt.title}</RadioCard.ItemText>
                        <RadioCard.ItemDescription>{opt.description}</RadioCard.ItemDescription>
                      </RadioCard.ItemContent>
                    </RadioCard.ItemControl>
                  </RadioCard.Item>
                ))}
              </Group>
            </RadioCard.Root>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
